import { addToast } from '@features/Prompt/notificationSlice.ts';
import i18n from '@providers/i18n';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  clearFallbackApiPreference,
  fallbackApiBasePath,
  getApiServerMode,
  hasFallbackApiBasePath,
  preferFallbackApi,
  shouldPreferFallbackApi,
} from '@shared/utils/apiEndpoint.ts';
import localStore from '@shared/utils/localStore.ts';
import { Mutex } from 'async-mutex';

import { applyAuthBootstrapRequestTimeout } from './authBootstrapRequest';
import { isParticipantCheckInStaffViewRequest } from './isParticipantCheckInRequest';
import { logout, updateTokens } from './reducers/authSlice.ts';
import { studentLogout, studentUpdateTokens } from './reducers/studentAuthSlice.ts';
import { trainerLogout, trainerUpdateTokens } from './reducers/trainerAuthSlice.ts';
import type { TokensDto } from './services/authApi.ts';
import type { StudentTokensDto } from './services/studentAuthApi.ts';
import type { TrainerTokensDto } from './services/trainerAuthApi.ts';

interface ErrorResponse {
  code: string;
  message: string;
  module: string;
}

interface AuthStateSnapshot {
  auth?: {
    tokens?: TokensDto;
    userId?: number;
  };
  studentAuth?: {
    tokens?: { accessToken: string | null; refreshToken: string | null };
    studentAccountId?: string;
  };
  trainerAuth?: {
    tokens?: { accessToken: string | null; refreshToken: string | null };
    trainerAccountId?: string;
  };
}

const mutex = new Mutex();

/** Admin routes like `/studentAccounts` or `/trainers` must not match portal apps. */
const isStudentApp = () => {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return pathname === '/student' || pathname.startsWith('/student/');
};

const isTrainerApp = () => {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return pathname === '/trainer' || pathname.startsWith('/trainer/');
};

const isPublicCheckInRequest = (args: string | FetchArgs) => {
  const requestUrl = typeof args === 'string' ? args : args.url;
  return /\/classes\/check-in\/(?:phone-session|session|post-training)\//.test(requestUrl);
};

const removeAuthorizationHeader = (headers: Headers) => {
  headers.delete('authorization');
  return headers;
};

const prepareHeaders = (headers: Headers) => {
  if (isStudentApp()) {
    const { accessToken } = localStore.getStudentTokens();
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  }

  if (isTrainerApp()) {
    const { accessToken } = localStore.getTrainerTokens();
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  }

  const { accessToken } = localStore.getTokens();
  if (accessToken) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }
  return headers;
};

const primaryBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_PATH,
  prepareHeaders,
});

const fallbackBaseQuery = fetchBaseQuery({
  baseUrl: fallbackApiBasePath,
  prepareHeaders,
});

const publicPrimaryBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_PATH,
  prepareHeaders: removeAuthorizationHeader,
});

const publicFallbackBaseQuery = fetchBaseQuery({
  baseUrl: fallbackApiBasePath,
  prepareHeaders: removeAuthorizationHeader,
});

const isFallbackError = (error?: FetchBaseQueryError) => {
  return error?.status === 'FETCH_ERROR' || error?.status === 'TIMEOUT_ERROR';
};

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const publicCheckInRequest = isPublicCheckInRequest(args);
  const serverMode = getApiServerMode();
  const preferFallback = shouldPreferFallbackApi();
  const primaryQuery = publicCheckInRequest ? publicPrimaryBaseQuery : primaryBaseQuery;
  const fallbackQuery = publicCheckInRequest ? publicFallbackBaseQuery : fallbackBaseQuery;
  const initialQuery = preferFallback ? fallbackQuery : primaryQuery;
  const retryQuery = preferFallback ? primaryQuery : fallbackQuery;

  let result = await initialQuery(args, api, extraOptions);

  if (!hasFallbackApiBasePath || serverMode !== 'auto' || !isFallbackError(result.error)) {
    return result;
  }

  const retryResult = await retryQuery(args, api, extraOptions);

  if (retryResult.error && isFallbackError(retryResult.error)) {
    return result;
  }

  if (preferFallback) {
    clearFallbackApiPreference();
  } else {
    preferFallbackApi();
  }

  result = retryResult;
  return result;
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const requestArgs = applyAuthBootstrapRequestTimeout(args);
  if (isPublicCheckInRequest(args)) {
    return baseQuery(requestArgs, api, extraOptions);
  }

  await mutex.waitForUnlock();
  const state = api.getState() as AuthStateSnapshot;
  const onStudentApp = isStudentApp();
  const onTrainerApp = isTrainerApp();

  const tokens = onStudentApp
    ? (state?.studentAuth?.tokens ?? localStore.getStudentTokens())
    : onTrainerApp
      ? (state?.trainerAuth?.tokens ?? localStore.getTrainerTokens())
      : (state?.auth?.tokens ?? localStore.getTokens());
  const accountId = onStudentApp
    ? (state?.studentAuth?.studentAccountId ?? localStore.getStudentAccountId())
    : onTrainerApp
      ? (state?.trainerAuth?.trainerAccountId ?? localStore.getTrainerAccountId())
      : (state?.auth?.userId ?? localStore.getUserId());

  let result = await baseQuery(requestArgs, api, extraOptions);

  const status = result.error?.status;

  if (
    status === 403 &&
    !onStudentApp &&
    !onTrainerApp &&
    !isParticipantCheckInStaffViewRequest(typeof args === 'string' ? args : args.url)
  ) {
    window.location.replace('/permissionDenied');
    return result;
  }

  if (result.error && Number(result.error.status) === 400) {
    const requestUrl = typeof requestArgs === 'string' ? requestArgs : requestArgs.url;
    const skipToast =
      (onStudentApp &&
        typeof requestUrl === 'string' &&
        requestUrl.includes('/studentAuth/signin')) ||
      (onTrainerApp &&
        typeof requestUrl === 'string' &&
        requestUrl.includes('/trainerAuth/signin'));

    if (!skipToast) {
      const errorResponse = result.error.data as ErrorResponse & {
        message?: string | string[];
      };
      const rawMessage = errorResponse?.message;
      const defaultMessage = Array.isArray(rawMessage)
        ? rawMessage.filter(Boolean).join(', ')
        : typeof rawMessage === 'string'
          ? rawMessage
          : 'Request failed';
      const message =
        errorResponse?.module && errorResponse?.code
          ? i18n.t(`${errorResponse.module}.errors.${errorResponse.code}`, {
              defaultValue: defaultMessage,
            })
          : defaultMessage;
      api.dispatch(addToast({ message: String(message), type: 'error' }));
    }

    return result;
  }

  if (result.error && Number(result.error.status) > 401) {
    const requestUrl = typeof requestArgs === 'string' ? requestArgs : requestArgs.url;
    const actionableEoiConflict =
      Number(result.error.status) === 409 &&
      typeof requestUrl === 'string' &&
      /\/trainerClasses\/[^/]+\/students\/[^/]+\/(?:eoi-verification|original-id-sighting|eoiTrainer)$/.test(
        requestUrl,
      );
    const data =
      'data' in result.error && result.error.data && typeof result.error.data === 'object'
        ? (result.error.data as {
            code?: string;
            message?: string | string[] | { code?: string; message?: string };
          })
        : undefined;
    const nested =
      data?.message && typeof data.message === 'object' && !Array.isArray(data.message)
        ? data.message
        : undefined;
    const code =
      (typeof data?.code === 'string' && data.code) ||
      (typeof nested?.code === 'string' && nested.code) ||
      undefined;
    const rawMessage = Array.isArray(data?.message)
      ? data.message.filter(Boolean).join(', ')
      : (typeof data?.message === 'string' && data.message) ||
        (typeof nested?.message === 'string' && nested.message) ||
        undefined;
    const mapped =
      !actionableEoiConflict &&
      code &&
      i18n.t(`trainerShell.errors.${code}`, {
        defaultValue: '',
      });
    const message = actionableEoiConflict
      ? rawMessage || 'The student information changed. Review the current step and try again.'
      : mapped
        ? String(mapped)
        : rawMessage
          ? `${rawMessage}, please try again later.`
          : 'Unable to reach the server, please try again later.';
    api.dispatch(addToast({ message, type: 'error' }));

    return result;
  }

  if (tokens?.accessToken && accountId && result.error && Number(result.error.status) === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshUrl = onStudentApp
          ? `/studentAuth/refresh/${accountId}`
          : onTrainerApp
            ? `/trainerAuth/refresh/${accountId}`
            : `/auth/refresh/${accountId}`;
        const refreshResult = await baseQuery(
          {
            url: refreshUrl,
            ...(typeof requestArgs !== 'string' && requestArgs.timeout !== undefined
              ? { timeout: requestArgs.timeout }
              : {}),
            headers: {
              'Content-Type': 'application/json',
              'Refresh-Token': tokens.refreshToken ?? undefined,
            },
            method: 'GET',
          },
          api,
          extraOptions,
        );
        if (refreshResult.data) {
          if (onStudentApp) {
            api.dispatch(studentUpdateTokens(refreshResult.data as StudentTokensDto));
          } else if (onTrainerApp) {
            api.dispatch(trainerUpdateTokens(refreshResult.data as TrainerTokensDto));
          } else {
            api.dispatch(updateTokens(refreshResult.data as TokensDto));
          }
          result = await baseQuery(requestArgs, api, extraOptions);
        } else if (onStudentApp) {
          api.dispatch(studentLogout());
        } else if (onTrainerApp) {
          api.dispatch(trainerLogout());
        } else {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(requestArgs, api, extraOptions);
    }
  } else if (result.error && Number(result.error.status) === 401) {
    if (onStudentApp) {
      api.dispatch(studentLogout());
    } else if (onTrainerApp) {
      api.dispatch(trainerLogout());
    } else {
      api.dispatch(logout());
    }
  }

  return result;
};

export const entrySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
